import React, { useState, useEffect } from "react";
import "./index.css";
import "./components/assets/App.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WBCChatBot from "./components/WBCChatBot";
import Devices from "./components/tabs/Devices";
import Overview from "./components/tabs/Overview";
import RemoteDebugSessionTabs from './components/tabs/RemoteDebugSessionTabs';
import DeviceRecovery from './components/tabs/DeviceRecovery';
import Repository from './components/tabs/Repository';
import Insights from './components/tabs/Insights';
import PlatformHealth from './components/tabs/PlatformHealth';
import AITab from './components/tabs/AITab';

import { CHATBOT_RESTRICTED_GROUPS } from './config/featureAccess';
import DeviceProfile from './components/tabs/DeviceProfile';
import FleetHealthTabs from './components/tabs/FleetHealthTabs';
import FleetHealthAnalytics from './components/tabs/FleetHealthAnalytics';
import NewDiagnostics from './components/tabs/NewDiagnostics';

function App({ userInfo, onLogout }) {

  const [selectedTab, setSelectedTab] = useState("Overview");
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [prefilledSystemId, setPrefilledSystemId] = useState("");
  const [deviceProfileContext, setDeviceProfileContext] = useState(null);
  const [utilitiesConfigAdId, setUtilitiesConfigAdId] = useState(null);
  // systemId + target to carry from Device sub-tab into New Diagnostics → Start RD/RT sub-tab
  // shape: { systemId: string, target: 'rd' | 'rt' } | null
  const [ndStartDevice, setNdStartDevice] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const userGroups = (userInfo && userInfo.groups) || [];
  const isChatbotRestricted = userGroups.some(g => CHATBOT_RESTRICTED_GROUPS.includes(g));
  const [isLoadingTabs, setIsLoadingTabs] = useState(true);
  const [tabAccessError, setTabAccessError] = useState(null);

  // Helper function to map internal tab names to display names
  const getDisplayTabName = (tabName) => {
    if (tabName === "Insights") return "AI Insights";
    return tabName;
  };

  const getInternalTabName = (displayName) => {
    if (displayName === "AI Insights") return "Insights";
    return displayName;
  };

  // Track authentication state (works for both Cognito and legacy Azure AD user shapes)
  const isAuthenticated = !!(userInfo && (userInfo.mail || userInfo.email) && (userInfo.displayName || userInfo.name));
  
  let userEmail = 'test@qti.qualcomm.com';
  let userDisplayName = 'Unknown User';

  if (userInfo) {
    userEmail = userInfo.mail || userInfo.email || userEmail;
    userDisplayName = userInfo.displayName || userInfo.name || userDisplayName;
  }

  const URI_FOR_TAB_ACCESS = process.env.REACT_APP_WBC_API_BASE_URL;
  const GET_COGNITO_USER_TABS_ACCESS = URI_FOR_TAB_ACCESS + '/api/v1/auth/cognito-user-role-tab-access';
  const GET_AZURE_USER_TABS_ACCESS = URI_FOR_TAB_ACCESS + '/api/v1/auth/user-role-tab-access';

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        // If not authenticated, clear tabs and stop loading
        setAllowedTabs([]);
        setUserRole("");
        setIsLoadingTabs(false);
        return;
      }

      try {
        setIsLoadingTabs(true);
        setTabAccessError(null);

        // Determine auth type and build appropriate URL
        const isAzureAuth = userInfo && userInfo.authType === 'azure';
        let uriForTabAccess;

        if (isAzureAuth) {
          // Azure AD users → LDAP-based endpoint
          uriForTabAccess = GET_AZURE_USER_TABS_ACCESS + '?loggedInUserEmailId=' + encodeURIComponent(userEmail);
        } else {
          // Cognito users → DB-based endpoint with group
          const cognitoGroup = (userInfo && userInfo.groups && userInfo.groups.length > 0)
            ? userInfo.groups[0]
            : '';
          uriForTabAccess =
            GET_COGNITO_USER_TABS_ACCESS +
            '?loggedInUserEmailId=' + encodeURIComponent(userEmail) +
            '&cognitoGroup=' + encodeURIComponent(cognitoGroup);
        }

        const response = await fetch(uriForTabAccess);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch tab access: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.roleTabsAccessMapping || data.roleTabsAccessMapping.length === 0) {
          console.error('No role mapping found for user');
          setAllowedTabs([]);
          setUserRole('');
          setTabAccessError('No access permissions found for your account. Please contact support.');
          return;
        }

        const roleTabsAccessMapping = data.roleTabsAccessMapping[0];
        const sortData = roleTabsAccessMapping.tabAccesses.sort();

        // Transform allowedTabs to include main tabs if any of their sub-tabs are present
        const transformedTabs = sortData.filter(t => t !== 'Home' && t !== 'License Hub' && t !== 'Config Management');
        
        // Handle Remote Diagnostics
        // Old "Diagnostics" tab retired (hidden from nav) in favor of "New Diagnostics",
        // which now carries the "Diagnostics" display label. Keep the render case +
        // components for a transition window; just stop surfacing it in the nav.
        // const hasRemoteDebugSubTabs = sortData.some(tab =>
        //   tab.startsWith("Remote Diagnostics Management/")
        // );
        // if (hasRemoteDebugSubTabs && !transformedTabs.includes("Diagnostics")) {
        //   transformedTabs.push("Diagnostics");
        // }
        
        // Handle Device Recovery - Commented out to hide from header as per user request
        // const hasDeviceRecoverySubTabs = sortData.some(tab => 
        //   tab.startsWith("Device Recovery/")
        // );
        // if (hasDeviceRecoverySubTabs && !transformedTabs.includes("Device Recovery")) {
        //   transformedTabs.push("Device Recovery");
        // }
        
        // Handle Devices
        const hasDevicesSubTabs = sortData.some(tab => 
          tab.startsWith("Devices/")
        );
        if (hasDevicesSubTabs && !transformedTabs.includes("Devices")) {
          transformedTabs.push("Devices");
        }
        
        // Handle Insights - Show if user has "AI Insights" access
        const hasAIInsightsAccess = sortData.includes("AI Insights");
        if (hasAIInsightsAccess && !transformedTabs.includes("Insights")) {
          transformedTabs.push("Insights");
        }

        if (!transformedTabs.includes("Overview")) {
          transformedTabs.push("Overview");
        }

        if (!transformedTabs.includes("Analytics")) {
          transformedTabs.push("Analytics");
        }

        if (!transformedTabs.includes("Anomaly Detection")) {
          transformedTabs.push("Anomaly Detection");
        }

        if (!transformedTabs.includes("New Diagnostics")) {
          transformedTabs.push("New Diagnostics");
        }

        if (!transformedTabs.includes("AI")) {
          transformedTabs.push("AI");
        }

        if (!transformedTabs.includes("Reports")) {
          transformedTabs.push("Reports");
        }

        setAllowedTabs(transformedTabs);
        setUserRole(roleTabsAccessMapping.role);

        // Default to Overview tab first
        setSelectedTab("Overview");
      } catch (error) {
        console.error('Error fetching tab access');
        // On error, clear tabs to prevent unauthorized access
        setAllowedTabs([]);
        setUserRole('');
        
        // Set user-friendly error message based on error type
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          setTabAccessError('Unable to connect to the server. Please check your network connection and try again.');
        } else if (error.message?.includes('401') || error.message?.includes('403')) {
          setTabAccessError('You do not have permission to access this portal. Please contact your administrator.');
        } else {
          setTabAccessError('An error occurred while loading your permissions. Please refresh the page or contact support.');
        }
      } finally {
        setIsLoadingTabs(false);
      }
    };

    fetchData();
  }, [isAuthenticated, userEmail]);


  const renderTabContent = () => {
    // Show loading state while fetching tab permissions
    if (isLoadingTabs) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          fontSize: '16px',
          color: '#666'
        }}>
          Loading...
        </div>
      );
    }

    // Show error message if tab access fetch failed
    if (tabAccessError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          fontSize: '18px',
          color: '#d32f2f',
          textAlign: 'center',
          gap: '10px',
          padding: '0 20px'
        }}>
          <p style={{ fontWeight: 'bold' }}>Error Loading Permissions</p>
          <p style={{ fontSize: '16px', color: '#666' }}>
            {tabAccessError}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    // If not authenticated, this should not normally be reached
    // (index.js shows LoginPage before rendering App)
    if (!isAuthenticated) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          fontSize: '18px',
          color: '#333',
        }}>
          <p>Please sign in to access the portal.</p>
        </div>
      );
    }

    // Check if user has access to the selected tab
    if (!allowedTabs.includes(selectedTab)) {
      return <div>Access Denied</div>;
    }

    switch (selectedTab) {
      case "Analytics":
        return (
          <div className="fh-tabs-container">
            <div className="fh-sub-content">
              <FleetHealthAnalytics
                userInfo={userEmail}
                role={userRole}
                onNavigateToConfigurations={(adId) => {
                  setUtilitiesConfigAdId(adId);
                  setSelectedTab("Utilities");
                }}
                onNavigateToDeviceProfile={(target) => {
                  setDeviceProfileContext(typeof target === 'object'
                    ? target
                    : { systemId: target });
                  setSelectedTab("Overview");
                }}
              />
            </div>
          </div>
        );
      case "Diagnostics":
        return <RemoteDebugSessionTabs userInfo={userEmail} role={userRole} allowedTabs={allowedTabs} prefilledSystemId={prefilledSystemId} setPrefilledSystemId={setPrefilledSystemId} setSelectedTab={setSelectedTab} userGroups={userGroups} />;
      case "Device Recovery":
        return <DeviceRecovery tab="Add" userInfo={userEmail} role={userRole} prefilledSystemId={prefilledSystemId} setPrefilledSystemId={setPrefilledSystemId} />;
      case "Telemetry":
        return <Repository
          tab="Telemetry"
          userInfo={userEmail}
          role={userRole}
        />;
      case "Utilities":
        return <Repository tab="Utilities" userInfo={userEmail} role={userRole} preselectedAdId={utilitiesConfigAdId} />;
      case "Insights":
        return <Insights userInfo={userEmail} role={userRole} />;
      case "AI":
        return <AITab userInfo={userEmail} role={userRole} />;
      case "Reports":
        return <PlatformHealth userInfo={userEmail} role={userRole} />;
      case "New Diagnostics":
        return (
          <NewDiagnostics
            userInfo={userEmail}
            role={userRole}
            userGroups={userGroups}
            setSelectedTab={setSelectedTab}
            setPrefilledSystemId={setPrefilledSystemId}
            ndStartDevice={ndStartDevice}
            setNdStartDevice={setNdStartDevice}
          />
        );
      default:
        return <Overview userInfo={userEmail} role={userRole} />;
    }
  };

  return (
    <div className="App">
      <Header
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        userName={userDisplayName}
        userEmail={userEmail}
        allowedTabs={allowedTabs}
        isAuthenticated={isAuthenticated}
        isLoadingTabs={isLoadingTabs}
        onLogout={onLogout}
      />
      
      
      <div className="body">
        {/* FleetHealthTabs stays mounted across tab switches so pre-fetched data is preserved */}
        {isAuthenticated && !isLoadingTabs && allowedTabs.includes("Overview") && (
          <div style={{ display: selectedTab === "Overview" ? 'block' : 'none' }}>
            <FleetHealthTabs
              userInfo={userEmail}
              role={userRole}
              deviceProfileContext={deviceProfileContext}
              deviceProfileSystemId={typeof prefilledSystemId === 'string' ? (prefilledSystemId || null) : null}
              onDeviceProfileConsumed={() => setPrefilledSystemId("")}
              onDeviceProfileContextConsumed={() => setDeviceProfileContext(null)}
              setNdStartDevice={setNdStartDevice}
              setSelectedTab={setSelectedTab}
            />
          </div>
        )}
        {selectedTab !== "Overview" && (
          (selectedTab === "Analytics")
            ? renderTabContent()
            : <div className="tab-body">{renderTabContent()}</div>
        )}
      </div>
      <Footer 
        className="footer" 
        onChatOpen={() => setIsChatOpen(true)}
        showChatButton={!isChatbotRestricted}
      />
      {!isChatbotRestricted && (
        <WBCChatBot 
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          userName={userDisplayName}
        />
      )}
    </div>
  );
}

export default App;
